/*
=========================================================
* RingFoods ROI Calculator Section
=========================================================
*/

import { useState, useMemo } from "react";

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Slider from "@mui/material/Slider";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";

// @mui icons
import CalculateIcon from "@mui/icons-material/Calculate";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PeopleIcon from "@mui/icons-material/People";

// Material Kit 2 PRO React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";
import { brandGradient, grey100 } from "pages/RingFoodsHomepage/theme";

// Animated counters
import CountUp from "react-countup";

import useSignupUrl from "hooks/useSignupUrl";

function ROICalculator() {
  const signupUrl = useSignupUrl();
  const [inputs, setInputs] = useState({
    dailyCalls: 25,
    missedCallPercentage: 20,
    averageTableValue: 75,
    staffHourlyWage: 15,
    hoursSpentOnPhone: 30,
  });

  // Calculations (memoized to avoid recalculating on every render)
  const calculations = useMemo(() => {
    const monthlyMissedCalls = Math.round((inputs.dailyCalls * 30 * inputs.missedCallPercentage) / 100);
    const potentialRecoveredRevenue = monthlyMissedCalls * inputs.averageTableValue * 0.8; // 80% conversion rate
    const monthlySalaryEavings = inputs.hoursSpentOnPhone * 4 * inputs.staffHourlyWage; // 4 weeks
    const staffHoursFreed = inputs.hoursSpentOnPhone * 4;
    const totalMonthlySavings = potentialRecoveredRevenue + monthlySalaryEavings;
    const ringFoodsCost = 200; // Professional plan - most common choice
    const netMonthlySavings = totalMonthlySavings - ringFoodsCost;
    const roiPercentage = Math.round((netMonthlySavings / ringFoodsCost) * 100);
    const annualSavings = netMonthlySavings * 12;
    return { monthlyMissedCalls, potentialRecoveredRevenue, monthlySalaryEavings, staffHoursFreed, totalMonthlySavings, ringFoodsCost, netMonthlySavings, roiPercentage, annualSavings };
  }, [inputs]);

  const handleInputChange = (field, value) => {
    setInputs((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <MKBox component="section" py={{ xs: 6, md: 12 }} sx={{ backgroundColor: grey100 }}>
      <Container>
        {/* Section Header */}
        <Grid container spacing={3} sx={{ mb: { xs: 4, md: 6 } }}>
          <Grid item xs={12} lg={8} mx="auto" textAlign="center">
            <MKTypography
              variant="h2"
              fontWeight="bold"
              color="primary"
              sx={{ mb: 2, fontSize: { xs: "1.75rem", md: "2.5rem" } }}
            >
              See What You&apos;re Really Losing to Phone Chaos
            </MKTypography>
            <MKTypography variant="body1" color="text" sx={{ fontSize: { xs: "0.95rem", md: "1.125rem" }, mb: 2 }}>
              Missed calls, wasted staff hours, and lost customers add up fast.
              Adjust the sliders to see the real cost for your restaurant.
            </MKTypography>
          </Grid>
        </Grid>

        <Grid container spacing={{ xs: 2, md: 4 }}>
          {/* Calculator Inputs */}
          <Grid item xs={12} lg={6}>
            <Card sx={{ height: "100%" }}>
              <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
                <MKBox sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <CalculateIcon sx={{ color: "primary.main", mr: 1 }} />
                  <MKTypography variant="h5" fontWeight="bold">
                    Your Restaurant Details
                  </MKTypography>
                </MKBox>

                <MKBox sx={{ mb: 4 }}>
                  <MKTypography variant="body1" fontWeight="bold" sx={{ mb: 2 }}>
                    Daily Phone Calls: {inputs.dailyCalls}
                  </MKTypography>
                  <Slider
                    aria-label="Daily phone calls"
                    value={inputs.dailyCalls}
                    onChange={(e, value) => handleInputChange("dailyCalls", value)}
                    min={5}
                    max={100}
                    marks={[
                      { value: 5, label: "5" },
                      { value: 25, label: "25" },
                      { value: 50, label: "50" },
                      { value: 100, label: "100+" },
                    ]}
                    sx={{ color: "primary.main" }}
                  />
                </MKBox>

                <MKBox sx={{ mb: 4 }}>
                  <MKTypography variant="body1" fontWeight="bold" sx={{ mb: 2 }}>
                    Missed Call Percentage: {inputs.missedCallPercentage}%
                  </MKTypography>
                  <Slider
                    aria-label="Missed call percentage"
                    value={inputs.missedCallPercentage}
                    onChange={(e, value) => handleInputChange("missedCallPercentage", value)}
                    min={0}
                    max={50}
                    marks={[
                      { value: 0, label: "0%" },
                      { value: 10, label: "10%" },
                      { value: 20, label: "20%" },
                      { value: 30, label: "30%" },
                      { value: 50, label: "50%" },
                    ]}
                    sx={{ color: "warning.main" }}
                  />
                </MKBox>

                <MKBox sx={{ mb: 4 }}>
                  <MKTypography component="label" htmlFor="avg-table-value" variant="body1" fontWeight="bold" sx={{ mb: 2, display: "block" }}>
                    Average Table Value
                  </MKTypography>
                  <TextField
                    id="avg-table-value"
                    fullWidth
                    type="number"
                    value={inputs.averageTableValue}
                    onChange={(e) => handleInputChange("averageTableValue", Number(e.target.value))}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    variant="outlined"
                    size="small"
                  />
                </MKBox>

                <MKBox sx={{ mb: 4 }}>
                  <MKTypography component="label" htmlFor="staff-hourly-wage" variant="body1" fontWeight="bold" sx={{ mb: 2, display: "block" }}>
                    Staff Hourly Wage
                  </MKTypography>
                  <TextField
                    id="staff-hourly-wage"
                    fullWidth
                    type="number"
                    value={inputs.staffHourlyWage}
                    onChange={(e) => handleInputChange("staffHourlyWage", Number(e.target.value))}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    variant="outlined"
                    size="small"
                  />
                </MKBox>

                <MKBox sx={{ mb: 3 }}>
                  <MKTypography variant="body1" fontWeight="bold" sx={{ mb: 2 }}>
                    Hours Spent on Phone Weekly: {inputs.hoursSpentOnPhone}
                  </MKTypography>
                  <Slider
                    aria-label="Hours spent on phone weekly"
                    value={inputs.hoursSpentOnPhone}
                    onChange={(e, value) => handleInputChange("hoursSpentOnPhone", value)}
                    min={5}
                    max={80}
                    marks={[
                      { value: 5, label: "5" },
                      { value: 20, label: "20" },
                      { value: 40, label: "40" },
                      { value: 80, label: "80" },
                    ]}
                    sx={{ color: "info.main" }}
                  />
                </MKBox>
              </CardContent>
            </Card>
          </Grid>

          {/* Results */}
          <Grid item xs={12} lg={6}>
            <Card
              sx={{
                height: "100%",
                background:
                  "linear-gradient(135deg, rgba(26, 26, 26, 0.03), rgba(51, 51, 51, 0.03))",
                border: "1px solid rgba(26, 26, 26, 0.1)",
              }}
            >
              <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
                {/* Annual Savings - Hero Number */}
                <MKBox
                  sx={{
                    background: "linear-gradient(135deg, #1A1A1A, #333333)",
                    borderRadius: 2,
                    p: { xs: 3, md: 4 },
                    mb: { xs: 2, md: 3 },
                    textAlign: "center",
                  }}
                >
                  <MKTypography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", fontWeight: 600, mb: 1, fontSize: { xs: "0.8rem", md: "0.875rem" } }}>
                    Estimated Annual Savings
                  </MKTypography>
                  <MKTypography variant="h2" component="p" fontWeight="bold" sx={{ color: "#22C55E", fontSize: { xs: "2.5rem", md: "3.5rem" }, lineHeight: 1 }}>
                    $<CountUp end={calculations.annualSavings} duration={2} separator="," preserveValue />
                  </MKTypography>
                  <MKTypography variant="body2" sx={{ color: "rgba(255,255,255,0.5)", mt: 1, fontSize: { xs: "0.75rem", md: "0.8rem" } }}>
                    per year
                  </MKTypography>
                </MKBox>

                {/* Monthly Breakdown */}
                <Grid container spacing={1.5} sx={{ mb: { xs: 2, md: 3 } }}>
                  <Grid item xs={6}>
                    <MKBox
                      sx={{
                        background: "white",
                        borderRadius: 2,
                        p: { xs: 1.5, md: 2 },
                        textAlign: "center",
                        border: "1px solid rgba(76, 175, 80, 0.2)",
                      }}
                    >
                      <AttachMoneyIcon sx={{ fontSize: { xs: 22, md: 28 }, color: "success.main", mb: 0.5 }} />
                      <MKTypography variant="h5" fontWeight="bold" color="success.main" sx={{ fontSize: { xs: "1.15rem", md: "1.5rem" } }}>
                        $<CountUp end={calculations.potentialRecoveredRevenue} duration={2} preserveValue />
                      </MKTypography>
                      <MKTypography variant="body2" fontWeight={600} sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}>
                        Revenue Recovery
                      </MKTypography>
                      <MKTypography variant="caption" color="text" sx={{ opacity: 0.6, fontSize: { xs: "0.6rem", md: "0.7rem" } }}>
                        {calculations.monthlyMissedCalls} recovered calls/mo
                      </MKTypography>
                    </MKBox>
                  </Grid>
                  <Grid item xs={6}>
                    <MKBox
                      sx={{
                        background: "white",
                        borderRadius: 2,
                        p: { xs: 1.5, md: 2 },
                        textAlign: "center",
                        border: "1px solid rgba(33, 150, 243, 0.2)",
                      }}
                    >
                      <PeopleIcon sx={{ fontSize: { xs: 22, md: 28 }, color: "info.main", mb: 0.5 }} />
                      <MKTypography variant="h5" fontWeight="bold" color="info.main" sx={{ fontSize: { xs: "1.15rem", md: "1.5rem" } }}>
                        <CountUp end={calculations.staffHoursFreed} duration={2} preserveValue />h
                      </MKTypography>
                      <MKTypography variant="body2" fontWeight={600} sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}>
                        Hours Freed
                      </MKTypography>
                      <MKTypography variant="caption" color="text" sx={{ opacity: 0.6, fontSize: { xs: "0.6rem", md: "0.7rem" } }}>
                        ${calculations.monthlySalaryEavings} labor savings/mo
                      </MKTypography>
                    </MKBox>
                  </Grid>
                </Grid>

                {/* ROI + Net */}
                <MKBox
                  sx={{
                    background: "white",
                    borderRadius: 2,
                    p: { xs: 1.5, md: 2 },
                    mb: { xs: 2, md: 3 },
                    textAlign: "center",
                    border: "1px solid rgba(229, 231, 235, 0.8)",
                    backgroundColor: "#f8fafc",
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <MKTypography variant="h4" fontWeight="bold" color="primary.main" sx={{ fontSize: { xs: "1.25rem", md: "1.75rem" } }}>
                        <CountUp end={calculations.roiPercentage} duration={2} preserveValue />%
                      </MKTypography>
                      <MKTypography variant="body2" color="text" sx={{ opacity: 0.7, fontSize: { xs: "0.7rem", md: "0.8rem" } }}>
                        ROI
                      </MKTypography>
                    </Grid>
                    <Grid item xs={6}>
                      <MKTypography variant="h4" fontWeight="bold" color="primary.main" sx={{ fontSize: { xs: "1.25rem", md: "1.75rem" } }}>
                        $<CountUp end={calculations.netMonthlySavings} duration={2} preserveValue />
                      </MKTypography>
                      <MKTypography variant="body2" color="text" sx={{ opacity: 0.7, fontSize: { xs: "0.7rem", md: "0.8rem" } }}>
                        Net Monthly Savings
                      </MKTypography>
                    </Grid>
                  </Grid>
                </MKBox>

                {/* Qualitative Callout */}
                <MKBox
                  sx={{
                    borderRadius: 2,
                    p: { xs: 1.5, md: 2 },
                    mb: { xs: 2, md: 3 },
                    textAlign: "center",
                    backgroundColor: "rgba(34, 197, 94, 0.06)",
                    border: "1px solid rgba(34, 197, 94, 0.15)",
                  }}
                >
                  <MKTypography variant="body2" color="text" sx={{ lineHeight: 1.6, fontSize: { xs: "0.75rem", md: "0.8rem" }, fontStyle: "italic", opacity: 0.8 }}>
                    Does not include the value of reduced staff turnover, improved Google reviews, and your peace of mind.
                  </MKTypography>
                </MKBox>

                {/* CTA */}
                <MKBox textAlign="center" sx={{ mt: { xs: 3, md: 4 } }}>
                  <MKButton
                    variant="contained"
                    size="large"
                    sx={{
                      backgroundColor: "#1A1A1A !important",
                      color: "#FFFFFF !important",
                      fontWeight: 600,
                      px: { xs: 3, md: 4 },
                      py: { xs: 1.25, md: 1.5 },
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "#333333 !important",
                      },
                    }}
                    onClick={() => window.location.href = signupUrl}
                  >
                    Start Your Free Trial
                  </MKButton>
                  <MKTypography variant="body2" color="text" sx={{ mt: 1, opacity: 0.7, fontSize: { xs: "0.75rem", md: "0.875rem" } }}>
                    See these results in your restaurant within 30 days
                  </MKTypography>
                </MKBox>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Bottom Note */}
        <Grid container spacing={3} sx={{ mt: { xs: 4, md: 6 } }}>
          <Grid item xs={12} textAlign="center">
            <MKBox
              sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 2,
                backgroundColor: "rgba(255, 152, 0, 0.1)",
                border: "1px solid rgba(255, 152, 0, 0.2)",
              }}
            >
              <MKTypography variant="body1" fontWeight="bold" sx={{ mb: 1, fontSize: { xs: "0.9rem", md: "1rem" } }}>
                Conservative Estimates
              </MKTypography>
              <MKTypography variant="body2" color="text" sx={{ opacity: 0.8, fontSize: { xs: "0.8rem", md: "0.875rem" } }}>
                Calculations are based on conservative industry averages. Actual results may vary
                and often exceed these projections. Does not include the value of reduced staff
                turnover, improved reviews, and your peace of mind.
              </MKTypography>
            </MKBox>
          </Grid>
        </Grid>
      </Container>
    </MKBox>
  );
}

export default ROICalculator;
